import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, CircularProgress, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ContentArea from '../components/content-area/Content';
import FreeTrialItemCard from '../components/items/FreeTrialItemCard';
import FreeTrialPackageCard from '../components/items/FreeTrialPackageCard';
import LocalText from '../components/common/LocalText';
import { useSpaceId } from '../hooks/useSpaceId';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../context/ToastContext';
import {
  getItems, getItemTypes,
  getFreeTrialSetting, getFreeTrialItems, addFreeTrialItem, removeFreeTrialItem,
  addFreeTrialPackage, removeFreeTrialPackage,
} from '../api/itemApi';
import { getPackages } from '../api/packageApi';

export default function FreeTrialAddItems() {
  const spaceId = useSpaceId();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/items', 'free-trial-edit');

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('items'); // 'items' | 'packages'
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [freeTrialSetting, setFreeTrialSetting] = useState(null);
  const [freeTrialItems, setFreeTrialItems] = useState([]);
  const [search, setSearch] = useState('');
  const [busyItemId, setBusyItemId] = useState(null);

  const load = useCallback(() => {
    if (!spaceId) return;
    setLoading(true);
    Promise.all([
      getItems().catch(() => null),
      getPackages().catch(() => null),
      getItemTypes().catch(() => null),
      getFreeTrialSetting().catch(() => null),
      getFreeTrialItems().catch(() => null),
    ]).then(([itemsData, packagesData, itTypes, settingData, freeTrialItemsData]) => {
      // Avatar items (itemTypeId 6) are hidden here too — same hotfix as Items.jsx:
      // avatars are now created/managed only from the Package "Create New Avatar" tab.
      if (Array.isArray(itemsData)) setItems(itemsData.filter(it => it.itemTypeId !== 6));
      if (Array.isArray(packagesData)) setPackages(packagesData);
      if (Array.isArray(itTypes)) setItemTypes(itTypes);
      setFreeTrialSetting(settingData ?? null);
      if (Array.isArray(freeTrialItemsData)) setFreeTrialItems(freeTrialItemsData);
    }).finally(() => setLoading(false));
  }, [spaceId]);

  useEffect(() => { load(); }, [load]);

  const freeTrialByItemId = useMemo(() => {
    const map = new Map();
    for (const fi of freeTrialItems) if (fi.itemId != null) map.set(fi.itemId, fi);
    return map;
  }, [freeTrialItems]);

  const freeTrialByPackageId = useMemo(() => {
    const map = new Map();
    for (const fi of freeTrialItems) if (fi.packageId != null) map.set(fi.packageId, fi);
    return map;
  }, [freeTrialItems]);

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const name = item.nameLocalizations?.values?.[0]?.value ?? item.name ?? '';
      return name.toLowerCase().includes(q) || (item.sku ?? '').toLowerCase().includes(q);
    });
  }, [items, search]);

  const filteredPackages = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return packages;
    return packages.filter((pkg) => {
      const name = pkg.nameLocalizations?.values?.[0]?.value ?? '';
      return name.toLowerCase().includes(q);
    });
  }, [packages, search]);

  const handleAdd = useCallback(async (itemId) => {
    setBusyItemId(itemId);
    try {
      await addFreeTrialItem(itemId);
      toast.success('Item added — submit it for approval from the Free Trial tab to go live.');
      const refreshed = await getFreeTrialItems().catch(() => null);
      if (Array.isArray(refreshed)) setFreeTrialItems(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to add the item to the free trial list.');
    } finally {
      setBusyItemId(null);
    }
  }, [toast]);

  const handleRemove = useCallback(async (itemId) => {
    setBusyItemId(itemId);
    try {
      const result = await removeFreeTrialItem(itemId);
      toast.success(result?.message || 'Item removed from the free trial list.');
      const refreshed = await getFreeTrialItems().catch(() => null);
      if (Array.isArray(refreshed)) setFreeTrialItems(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to remove the item from the free trial list.');
    } finally {
      setBusyItemId(null);
    }
  }, [toast]);

  const handleAddPackage = useCallback(async (packageId) => {
    setBusyItemId(packageId);
    try {
      await addFreeTrialPackage(packageId);
      toast.success('Package added — submit it for approval from the Free Trial tab to go live.');
      const refreshed = await getFreeTrialItems().catch(() => null);
      if (Array.isArray(refreshed)) setFreeTrialItems(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to add the package to the free trial list.');
    } finally {
      setBusyItemId(null);
    }
  }, [toast]);

  const handleRemovePackage = useCallback(async (packageId) => {
    setBusyItemId(packageId);
    try {
      const result = await removeFreeTrialPackage(packageId);
      toast.success(result?.message || 'Package removed from the free trial list.');
      const refreshed = await getFreeTrialItems().catch(() => null);
      if (Array.isArray(refreshed)) setFreeTrialItems(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to remove the package from the free trial list.');
    } finally {
      setBusyItemId(null);
    }
  }, [toast]);

  // No campaign yet — nothing to add items to. Send the marketer back to create one first.
  if (!loading && !freeTrialSetting) {
    return (
      <ContentArea title="Add Free Trial Items" icon={<CardGiftcardOutlinedIcon />}>
        <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', p: 4, textAlign: 'center' }}>
          <Typography fontWeight={700} fontSize={14} sx={{ color: '#334155', mb: 1 }}>
            <LocalText>No free trial campaign yet</LocalText>
          </Typography>
          <Typography fontSize={12} sx={{ color: '#94a3b8', mb: 2.5 }}>
            <LocalText>Create a campaign on the Free Trial tab before selecting items.</LocalText>
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/items', { state: { activeTab: 'freeTrial' } })}
            sx={{ background: '#0a326d', color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none', borderRadius: 2, px: 3, py: 1, '&:hover': { background: '#0d3d85' } }}
          >
            <LocalText>Back to Free Trial Campaign</LocalText>
          </Button>
        </Box>
      </ContentArea>
    );
  }

  return (
    <ContentArea title="Add Free Trial Items" icon={<CardGiftcardOutlinedIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={tab}
            onChange={(_, v) => v && setTab(v)}
            sx={{ bgcolor: 'white', borderRadius: '10px' }}
          >
            <ToggleButton value="items" sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, px: 2 }}>
              <LocalText>Items</LocalText>
            </ToggleButton>
            <ToggleButton value="packages" sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, px: 2 }}>
              <LocalText>Packages</LocalText>
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder={tab === 'items' ? 'Search items...' : 'Search packages...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            sx={{ bgcolor: 'white', borderRadius: '10px', minWidth: 260, '& fieldset': { borderRadius: '10px' } }}
          />
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate('/items', { state: { activeTab: 'freeTrial' } })}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: 12 }}
        >
          <LocalText>Done — Back to Campaign</LocalText>
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : tab === 'items' ? (
        filteredItems.length === 0 ? (
          <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', p: 4, textAlign: 'center' }}>
            <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
              <LocalText>{items.length === 0 ? 'No items yet — add items in the Item Catalog tab first.' : 'No items match your search.'}</LocalText>
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
            {filteredItems.map((item) => (
              <FreeTrialItemCard
                key={item.itemId}
                item={item}
                itemTypes={itemTypes}
                freeTrialEntry={freeTrialByItemId.get(item.itemId)}
                onAdd={handleAdd}
                onRemove={handleRemove}
                canEdit={canEdit}
                busy={busyItemId === item.itemId}
              />
            ))}
          </Box>
        )
      ) : (
        filteredPackages.length === 0 ? (
          <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', p: 4, textAlign: 'center' }}>
            <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
              <LocalText>{packages.length === 0 ? 'No packages yet — create one in the Packages tab first.' : 'No packages match your search.'}</LocalText>
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
            {filteredPackages.map((pkg) => (
              <FreeTrialPackageCard
                key={pkg.packageId}
                pkg={pkg}
                freeTrialEntry={freeTrialByPackageId.get(pkg.packageId)}
                onAdd={handleAddPackage}
                onRemove={handleRemovePackage}
                canEdit={canEdit}
                busy={busyItemId === pkg.packageId}
              />
            ))}
          </Box>
        )
      )}
    </ContentArea>
  );
}
